import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode
} from "react";
import { createPortal } from "react-dom";
import { CheckCircle, Info, WarningCircle, XCircle } from "@phosphor-icons/react";

import { defaultIconProps } from "../features/shared/ui/icons";
import { useSwipeToDismiss } from "../hooks/useSwipeToDismiss";

type ToastTone = "info" | "success" | "warning" | "error";

type ToastOptions = {
  id?: string;
  message: string;
  tone?: ToastTone;
  duration?: number;
};

type ToastInstance = Required<Omit<ToastOptions, "id">> & { id: string };

interface ToastContextValue {
  show: (options: ToastOptions) => string;
  dismiss: (id: string) => void;
  info: (message: string, options?: Partial<Omit<ToastOptions, "message" | "tone">>) => string;
  success: (message: string, options?: Partial<Omit<ToastOptions, "message" | "tone">>) => string;
  warning: (message: string, options?: Partial<Omit<ToastOptions, "message" | "tone">>) => string;
  error: (message: string, options?: Partial<Omit<ToastOptions, "message" | "tone">>) => string;
}

const DEFAULT_DURATION = 5000;

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastInstance[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback(
    (options: ToastOptions) => {
      const id =
        options.id ??
        (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
      const tone: ToastTone = options.tone ?? "info";
      const duration = options.duration ?? DEFAULT_DURATION;
      const toast: ToastInstance = {
        id,
        message: options.message,
        tone,
        duration
      };

      setToasts((current) => [...current, toast]);

      if (duration > 0) {
        window.setTimeout(() => dismiss(id), duration);
      }

      return id;
    },
    [dismiss]
  );

  const typedShow = useCallback(
    (tone: ToastTone) =>
      (message: string, options?: Partial<Omit<ToastOptions, "message" | "tone">>) =>
        show({ ...options, message, tone }),
    [show]
  );

  const value = useMemo<ToastContextValue>(
    () => ({
      show,
      dismiss,
      info: typedShow("info"),
      success: typedShow("success"),
      warning: typedShow("warning"),
      error: typedShow("error")
    }),
    [dismiss, show, typedShow]
  );

  const portalTarget = typeof document !== "undefined" ? document.body : null;

  return (
    <ToastContext.Provider value={value}>
      {children}
      {portalTarget &&
        createPortal(
          <ToastViewport dismiss={dismiss} toasts={toasts} />,
          portalTarget
        )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}

function ToastViewport({ toasts, dismiss }: { toasts: ToastInstance[]; dismiss: (id: string) => void }) {
  if (toasts.length === 0) {
    return null;
  }

  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-4 z-[60] flex flex-col items-center gap-3 px-4 sm:top-6 sm:right-8 sm:left-auto sm:items-end sm:w-full sm:max-w-sm"
      role="status"
      aria-live="polite"
    >
      {toasts.map((toast) => (
        <ToastMessage key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
      ))}
    </div>
  );
}

function ToastMessage({ toast, onDismiss }: { toast: ToastInstance; onDismiss: () => void }) {
  const iconProps = { ...defaultIconProps, className: "h-5 w-5" };

  const ToneIcon = useMemo(() => {
    switch (toast.tone) {
      case "success":
        return CheckCircle;
      case "warning":
        return WarningCircle;
      case "error":
        return XCircle;
      default:
        return Info;
    }
  }, [toast.tone]);

  const toneStyles: Record<
    ToastTone,
    { container: string; iconWrap: string; text: string; dismiss: string }
  > = {
    info: {
      container: "border-sky-200/50 bg-white/90 shadow-sky-900/10",
      iconWrap: "bg-sky-100 text-sky-600",
      text: "text-slate-700",
      dismiss: "text-slate-400 hover:bg-slate-100 hover:text-slate-600",
    },
    success: {
      container: "border-emerald-200/50 bg-white/90 shadow-emerald-900/10",
      iconWrap: "bg-emerald-100 text-emerald-600",
      text: "text-slate-700",
      dismiss: "text-slate-400 hover:bg-slate-100 hover:text-slate-600",
    },
    warning: {
      container: "border-amber-200/50 bg-white/90 shadow-amber-900/10",
      iconWrap: "bg-amber-100 text-amber-600",
      text: "text-slate-700",
      dismiss: "text-slate-400 hover:bg-slate-100 hover:text-slate-600",
    },
    error: {
      container: "border-rose-200/50 bg-white/90 shadow-rose-900/10",
      iconWrap: "bg-rose-100 text-rose-600",
      text: "text-slate-700",
      dismiss: "text-slate-400 hover:bg-slate-100 hover:text-slate-600",
    },
  };

  const toneClass = toneStyles[toast.tone];
  const { swipeHandlers, swipeStyle, isSwiping } = useSwipeToDismiss({
    onDismiss,
    threshold: 80,
  });

  return (
    <div role="status" aria-live="polite" className="w-full animate-in slide-in-from-bottom-5 fade-in duration-300">
      <div
        className={[
          "pointer-events-auto flex w-full items-center gap-3 rounded-2xl border p-3 shadow-lg backdrop-blur-xl transition-all duration-200 ease-out cursor-grab active:cursor-grabbing touch-pan-y active:scale-[0.98]",
          isSwiping ? "select-none" : "",
          isSwiping ? "transition-none" : "",
          toneClass.container,
        ].join(" ")}
        style={swipeStyle}
        {...swipeHandlers}
      >
        <div
          className={[
            "inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full",
            toneClass.iconWrap,
          ].join(" ")}
          aria-hidden="true"
        >
          <ToneIcon {...iconProps} weight="fill" />
        </div>
        <p className={["flex-1 text-[15px] font-medium leading-snug", toneClass.text].join(" ")}>{toast.message}</p>
        <button
          type="button"
          className={[
            "ml-1 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-400",
            toneClass.dismiss,
          ].join(" ")}
          onClick={onDismiss}
          aria-label="Close notification"
          data-swipe-dismiss-ignore
          onPointerDown={(event) => event.stopPropagation()}
          onPointerUp={(event) => event.stopPropagation()}
        >
          <span className="text-xl leading-none">&times;</span>
        </button>
      </div>
    </div>
  );
}
